// Action creators for the commission agents slice.
//
// One shared factory, one line per action. The creator bodies are not repeated,
// and every export is statically analysable so a typo'd import fails at build
// time rather than resolving to undefined at runtime.

import * as types from '../../lib/actionTypes.es6';

function creator(type) {
  return function create(payload) {
    return { type: type, payload: payload };
  };
}

// ─── Agents list, detail, bulk resolve ───────────────────────────────────────

export var getCommissionAgentsRequest =
  creator(types.GET_COMMISSION_AGENTS_REQUEST);
export var getCommissionAgentsSuccess =
  creator(types.GET_COMMISSION_AGENTS_SUCCESS);
export var getCommissionAgentsFailed =
  creator(types.GET_COMMISSION_AGENTS_FAILURE);

export var getCommissionAgentRequest =
  creator(types.GET_COMMISSION_AGENT_REQUEST);
export var getCommissionAgentSuccess =
  creator(types.GET_COMMISSION_AGENT_SUCCESS);
export var getCommissionAgentFailed =
  creator(types.GET_COMMISSION_AGENT_FAILURE);

// Resolves only the guids missing from the byGuid cache — one request for a
// whole table of rows, never one per row.
export var resolveCommissionAgentsRequest =
  creator(types.RESOLVE_COMMISSION_AGENTS_REQUEST);
export var resolveCommissionAgentsSuccess =
  creator(types.RESOLVE_COMMISSION_AGENTS_SUCCESS);
export var resolveCommissionAgentsFailed =
  creator(types.RESOLVE_COMMISSION_AGENTS_FAILURE);

// ─── Agent writes ────────────────────────────────────────────────────────────

export var createCommissionAgentRequest =
  creator(types.CREATE_COMMISSION_AGENT_REQUEST);
export var createCommissionAgentSuccess =
  creator(types.CREATE_COMMISSION_AGENT_SUCCESS);
export var createCommissionAgentFailed =
  creator(types.CREATE_COMMISSION_AGENT_FAILURE);
export var resetCreateCommissionAgentSuccess =
  creator(types.RESET_CREATE_COMMISSION_AGENT_SUCCESS);
export var resetCreateCommissionAgentError =
  creator(types.RESET_CREATE_COMMISSION_AGENT_ERROR);

export var updateCommissionAgentRequest =
  creator(types.UPDATE_COMMISSION_AGENT_REQUEST);
export var updateCommissionAgentSuccess =
  creator(types.UPDATE_COMMISSION_AGENT_SUCCESS);
export var updateCommissionAgentFailed =
  creator(types.UPDATE_COMMISSION_AGENT_FAILURE);
export var resetUpdateCommissionAgentSuccess =
  creator(types.RESET_UPDATE_COMMISSION_AGENT_SUCCESS);
export var resetUpdateCommissionAgentError =
  creator(types.RESET_UPDATE_COMMISSION_AGENT_ERROR);

// Soft delete — marks the agent inactive.
export var deleteCommissionAgentRequest =
  creator(types.DELETE_COMMISSION_AGENT_REQUEST);
export var deleteCommissionAgentSuccess =
  creator(types.DELETE_COMMISSION_AGENT_SUCCESS);
export var deleteCommissionAgentFailed =
  creator(types.DELETE_COMMISSION_AGENT_FAILURE);
export var resetDeleteCommissionAgentSuccess =
  creator(types.RESET_DELETE_COMMISSION_AGENT_SUCCESS);
export var resetDeleteCommissionAgentError =
  creator(types.RESET_DELETE_COMMISSION_AGENT_ERROR);

// Hard delete — irreversible, behind a typed confirmation.
export var deleteCommissionAgentPermanentRequest =
  creator(types.DELETE_COMMISSION_AGENT_PERMANENT_REQUEST);
export var deleteCommissionAgentPermanentSuccess =
  creator(types.DELETE_COMMISSION_AGENT_PERMANENT_SUCCESS);
export var deleteCommissionAgentPermanentFailed =
  creator(types.DELETE_COMMISSION_AGENT_PERMANENT_FAILURE);
export var resetDeleteCommissionAgentPermanent =
  creator(types.RESET_DELETE_COMMISSION_AGENT_PERMANENT);

export var setCommissionAgentToEdit =
  creator(types.SET_COMMISSION_AGENT_TO_EDIT);
export var resetCommissionAgentToEdit =
  creator(types.RESET_COMMISSION_AGENT_TO_EDIT);

// Creating an agent who is not on the platform yet: create the Keycloak user,
// then the agent keyed to that user's id. Payload: { user, agent }.
export var createAgentWithUserRequest =
  creator(types.CREATE_AGENT_WITH_USER_REQUEST);
export var createAgentWithUserSuccess =
  creator(types.CREATE_AGENT_WITH_USER_SUCCESS);
export var createAgentWithUserFailed =
  creator(types.CREATE_AGENT_WITH_USER_FAILURE);
// The user was created but the agent was not — a state the UI must distinguish
// from a plain failure, or a retry would create a second user.
export var createAgentWithUserPartial =
  creator(types.CREATE_AGENT_WITH_USER_PARTIAL);

// ─── Lookups and existence check ─────────────────────────────────────────────

// Drives the disabled rows in step 1 of the add-agent wizard.
export var commissionAgentExistsRequest =
  creator(types.COMMISSION_AGENT_EXISTS_REQUEST);
export var commissionAgentExistsSuccess =
  creator(types.COMMISSION_AGENT_EXISTS_SUCCESS);
export var commissionAgentExistsFailed =
  creator(types.COMMISSION_AGENT_EXISTS_FAILURE);

// Free-text search by name, email or agent code. Payload: { value }
export var lookupCommissionAgentRequest =
  creator(types.LOOKUP_COMMISSION_AGENT_REQUEST);
export var lookupCommissionAgentSuccess =
  creator(types.LOOKUP_COMMISSION_AGENT_SUCCESS);
export var lookupCommissionAgentFailed =
  creator(types.LOOKUP_COMMISSION_AGENT_FAILURE);
export var resetLookupCommissionAgent =
  creator(types.RESET_LOOKUP_COMMISSION_AGENT);

// ─── Platform users (step 1 of the wizard) ───────────────────────────────────

export var getUsersForAgentsRequest =
  creator(types.GET_USERS_FOR_AGENTS_REQUEST);
export var getUsersForAgentsSuccess =
  creator(types.GET_USERS_FOR_AGENTS_SUCCESS);
export var getUsersForAgentsFailed =
  creator(types.GET_USERS_FOR_AGENTS_FAILURE);

// ─── Agent bank details (agent-scoped) ───────────────────────────────────────

export var getAgentBanksByAgentRequest =
  creator(types.GET_AGENT_BANKS_BY_AGENT_REQUEST);
export var getAgentBanksByAgentSuccess =
  creator(types.GET_AGENT_BANKS_BY_AGENT_SUCCESS);
export var getAgentBanksByAgentFailed =
  creator(types.GET_AGENT_BANKS_BY_AGENT_FAILURE);

export var createAgentBankRequest = creator(types.CREATE_AGENT_BANK_REQUEST);
export var createAgentBankSuccess = creator(types.CREATE_AGENT_BANK_SUCCESS);
export var createAgentBankFailed = creator(types.CREATE_AGENT_BANK_FAILURE);
export var resetCreateAgentBankSuccess =
  creator(types.RESET_CREATE_AGENT_BANK_SUCCESS);
export var resetCreateAgentBankError =
  creator(types.RESET_CREATE_AGENT_BANK_ERROR);

export var updateAgentBankRequest = creator(types.UPDATE_AGENT_BANK_REQUEST);
export var updateAgentBankSuccess = creator(types.UPDATE_AGENT_BANK_SUCCESS);
export var updateAgentBankFailed = creator(types.UPDATE_AGENT_BANK_FAILURE);
export var resetUpdateAgentBankSuccess =
  creator(types.RESET_UPDATE_AGENT_BANK_SUCCESS);
export var resetUpdateAgentBankError =
  creator(types.RESET_UPDATE_AGENT_BANK_ERROR);

export var deleteAgentBankRequest = creator(types.DELETE_AGENT_BANK_REQUEST);
export var deleteAgentBankSuccess = creator(types.DELETE_AGENT_BANK_SUCCESS);
export var deleteAgentBankFailed = creator(types.DELETE_AGENT_BANK_FAILURE);

export var deleteAgentBankPermanentRequest =
  creator(types.DELETE_AGENT_BANK_PERMANENT_REQUEST);
export var deleteAgentBankPermanentSuccess =
  creator(types.DELETE_AGENT_BANK_PERMANENT_SUCCESS);
export var deleteAgentBankPermanentFailed =
  creator(types.DELETE_AGENT_BANK_PERMANENT_FAILURE);
